import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, Button } from '../components';
import { CourrierAssignment, WorkflowTransition, WorkflowHistory } from '../components';
import { usePieces, useAuth } from '../hooks';
import { queryKeys } from '../lib/react-query';
import { courrierService, piecesService } from '../services/api';
import { ImageModal } from '../components/ImageModal';
import { Modal } from '../components';
import { CourrierStatus, CourrierPriority, UpdateCourrierRequest } from '../types/api';
import { apiClient } from '../services/api/client';
import { formatDateTimeForInput, formatDateTimeForDisplay } from '../utils/dateTime';

export function CourrierDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const courrierId = parseInt(id || '0');
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<'details' | 'pieces' | 'history'>('details');
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalSrc, setImageModalSrc] = useState('');
  const [imageModalTitle, setImageModalTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  
  // États pour la modale OCR
  const [ocrModalOpen, setOcrModalOpen] = useState(false);
  const [ocrModalTitle, setOcrModalTitle] = useState('');
  const [ocrText, setOcrText] = useState('');
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState('');
  const [ocrConfidence, setOcrConfidence] = useState<number | null>(null);
  const [ocrStatus, setOcrStatus] = useState<string | null>(null);
  const [ocrCopyOk, setOcrCopyOk] = useState(false);

  // Upload pièce depuis la page détail
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingPiece, setUploadingPiece] = useState(false);

  const handleAddPieceClick = () => {
    fileInputRef.current?.click();
  };

  const handlePieceFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    console.log('📎 [COURRIER DETAIL] Upload de pièce jointe:', file.name, 'taille:', file.size, 'type:', file.type);
    try {
      setUploadingPiece(true);
      console.log('📎 [COURRIER DETAIL] Appel de piecesService.uploadPiece pour le courrier:', courrierId);
      await piecesService.uploadPiece(courrierId, file);
      console.log('📎 [COURRIER DETAIL] Upload réussi, rechargement des pièces...');
      await loadPieces(courrierId);
      console.log('📎 [COURRIER DETAIL] Pièces rechargées');
    } catch (err: any) {
      console.error('❌ [COURRIER DETAIL] Erreur upload pièce depuis détail:', err);
      console.error('❌ [COURRIER DETAIL] Détails de l\'erreur:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
    } finally {
      setUploadingPiece(false);
      e.target.value = '';
    }
  };

  // Récupérer les détails du courrier
  const { data: courrier, isLoading: courrierLoading } = useQuery({
    queryKey: queryKeys.courriers.detail(courrierId),
    queryFn: () => courrierService.getCourrier(courrierId),
    enabled: !!courrierId,
  });

  // Récupérer les informations des utilisateurs (créateur et assigné)
  const { data: usersData } = useQuery({
    queryKey: ['users', 'perimeter'],
    queryFn: () => apiClient.get('/users/perimeter'),
    enabled: !!courrier,
  });

  // Fonction pour obtenir le nom d'utilisateur par ID
  const getUserName = (userId: number | null | undefined): string => {
    if (!userId || !usersData) return '-';
    const user = usersData.find((u: any) => u.id === userId);
    return user?.fullName || `Utilisateur ${userId}`;
  };

  // Récupérer les pièces jointes
  const {
    pieces,
    isLoading: piecesLoading,
    loadPieces,
    downloadPiece,
    triggerOCR,
    getOCRText,
  } = usePieces();

  // Récupérer l'historique des événements de workflow
  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['courrier', 'workflow-events', courrierId],
    queryFn: async () => {
      const response = await apiClient.get(`/courriers/${courrierId}/workflow-events`);
      return response.data || [];
    },
    enabled: !!courrierId,
  });

  // Mutation pour changer le statut
  const transitionMutation = useMutation({
    mutationFn: ({ toStatus, comment }: { toStatus: CourrierStatus; comment?: string }) =>
      courrierService.transitionCourrier(courrierId, { to_status: toStatus, comment }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.detail(courrierId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.lists() });
    },
  });

  // Mutation pour assigner
  const assignMutation = useMutation({
    mutationFn: ({ assignedTo, comment }: { assignedTo: number; comment?: string }) =>
      courrierService.assignCourrier(courrierId, { assignedTo, comment }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.detail(courrierId) });
    },
  });

  // Mutation pour mettre à jour le courrier
  const updateMutation = useMutation({
    mutationFn: (updates: UpdateCourrierRequest) =>
      courrierService.updateCourrier(courrierId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.detail(courrierId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.courriers.lists() });
      setEditModalOpen(false);
    },
  });

  React.useEffect(() => {
    if (courrierId) {
      loadPieces(courrierId);
    }
  }, [courrierId, loadPieces]);

  const getStatusColor = (status: CourrierStatus) => {
    const colors = {
      recu: 'bg-blue-100 text-blue-800',
      en_validation: 'bg-yellow-100 text-yellow-800',
      incomplet: 'bg-red-100 text-red-800',
      en_attente_signature: 'bg-purple-100 text-purple-800',
      signe: 'bg-green-100 text-green-800',
      envoye: 'bg-indigo-100 text-indigo-800',
      classe: 'bg-gray-100 text-gray-800',
      rejete: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority: CourrierPriority) => {
    const colors = {
      basse: 'bg-gray-100 text-gray-800',
      normale: 'bg-blue-100 text-blue-800',
      haute: 'bg-orange-100 text-orange-800',
      urgente: 'bg-red-100 text-red-800',
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const handleStatusChange = (newStatus: CourrierStatus) => {
    transitionMutation.mutate({
      toStatus: newStatus,
      comment: `Changement de statut vers ${newStatus}`,
    });
  };

  if (courrierLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!courrier) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Courrier non trouvé</h2>
        <p className="text-gray-600 mt-2">Le courrier demandé n'existe pas.</p>
        <Button onClick={() => navigate('/courriers')} className="mt-4">
          Retour à la liste
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ImageModal
        isOpen={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        src={imageModalSrc}
        title={imageModalTitle}
        mimeType={pieces.find(p => (p.original_name || p.filename) === imageModalTitle)?.mime}
      />
      
      {/* Modale OCR */}
      <Modal
        isOpen={ocrModalOpen}
        onClose={() => setOcrModalOpen(false)}
        title={`OCR - ${ocrModalTitle}`}
        size="large"
      >
        <div className="space-y-4">
          {/* Bandeau statut */}
          {(ocrStatus || ocrLoading) && (
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-4 py-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">Statut:</span>
                <span
                  className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                    ocrLoading || ocrStatus === 'PENDING' || ocrStatus === 'PROCESSING'
                      ? 'bg-yellow-100 text-yellow-800'
                      : ocrStatus === 'COMPLETED'
                      ? 'bg-green-100 text-green-800'
                      : ocrStatus === 'FAILED'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {ocrLoading ? 'TRAITEMENT...' : (ocrStatus ?? '—')}
                </span>
              </div>
              {ocrConfidence != null && !ocrLoading && (
                <div className="text-sm text-gray-600">
                  Confiance: <span className="font-semibold">{Math.round(ocrConfidence * 100) / 100}</span>
                </div>
              )}
            </div>
          )}

          {ocrLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              <p className="ml-3 text-gray-600">Traitement OCR en cours...</p>
            </div>
          )}

          {ocrError && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
              {ocrError}
            </div>
          )}

          {!ocrLoading && !ocrError && ocrText && (
            <div>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Texte reconnu</h3>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(ocrText);
                        setOcrCopyOk(true);
                        setTimeout(() => setOcrCopyOk(false), 2000);
                      } catch {
                        setOcrCopyOk(false);
                      }
                    }}
                  >
                    Copier le texte
                  </Button>
                  {ocrCopyOk && <span className="text-xs text-green-600">Copié</span>}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded border border-gray-200 max-h-96 overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-gray-800">{ocrText}</pre>
              </div>
            </div>
          )}

          {!ocrLoading && !ocrError && !ocrText && (
            <div className="text-center py-8 text-gray-500">
              Aucun texte n'a été reconnu dans cette image.
            </div>
          )}
        </div>
      </Modal>
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigate('/courriers')}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                ← Retour
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{courrier.subject}</h1>
                <p className="text-sm text-gray-600">Référence: {courrier.reference}</p>
              </div>
            </div>
          </div>
          <div className="flex space-x-2">
            {(user?.id === courrier?.assignedTo || user?.id === courrier?.createdBy ||
              user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
              <Button variant="outline" size="sm" onClick={() => setEditModalOpen(true)}>
                Modifier
              </Button>
            )}
          </div>
        </div>

        {/* Informations principales */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-6">
            {/* Détails du courrier */}
            <Card title="Informations">
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Expéditeur</label>
                    <p className="mt-1 text-sm text-gray-900">{courrier.sender}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Destinataire</label>
                    <p className="mt-1 text-sm text-gray-900">{courrier.recipient}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Statut</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getStatusColor(courrier.status)}`}>
                      {courrier.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Priorité</label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getPriorityColor(courrier.priority)}`}>
                      {courrier.priority}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date de réception</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(courrier.receivedAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Échéance</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {courrier.dueDate ? formatDateTimeForDisplay(courrier.dueDate) : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assigné à</label>
                    <CourrierAssignment
                      courrierId={courrier.id}
                      currentAssignedTo={courrier.assignedTo}
                    />
                  </div>
                </div>

                {courrier.summaryText && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Résumé OCR</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded">
                      {courrier.summaryText}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Onglets */}
            <div className="space-y-4">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  {[
                    { id: 'details', name: 'Détails' },
                    { id: 'pieces', name: 'Pièces jointes' },
                    { id: 'history', name: 'Historique' },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.name}
                    </button>
                  ))}
                </nav>
              </div>

              <div className="tab-content">
                {activeTab === 'details' && (
                  <Card title="Détails du courrier">
                    <CardContent className="space-y-6">
                      {/* Informations générales */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Référence</label>
                          <p className="mt-1 text-sm text-gray-900">{courrier.reference}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Type</label>
                          <p className="mt-1 text-sm text-gray-900">{courrier.type}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Expéditeur</label>
                          <p className="mt-1 text-sm text-gray-900">{courrier.sender}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Destinataire</label>
                          <p className="mt-1 text-sm text-gray-900">{courrier.recipient}</p>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">Sujet</label>
                          <p className="mt-1 text-sm text-gray-900">{courrier.subject}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Statut</label>
                          <p className="mt-1 text-sm text-gray-900">{courrier.status}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Priorité</label>
                          <p className="mt-1 text-sm text-gray-900">{courrier.priority}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Reçu le</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {courrier.receivedAt ? new Date(courrier.receivedAt).toLocaleString('fr-FR') : '-'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Échéance</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {courrier.dueDate ? formatDateTimeForDisplay(courrier.dueDate) : '-'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Organisation</label>
                          <p className="mt-1 text-sm text-gray-900">{courrier.organisationId ?? '-'}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Créé par</label>
                          <p className="mt-1 text-sm text-gray-900">{getUserName(courrier.createdBy)}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Créé le</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {courrier.createdAt ? new Date(courrier.createdAt).toLocaleString('fr-FR') : '-'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Modifié le</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {courrier.updatedAt ? new Date(courrier.updatedAt).toLocaleString('fr-FR') : '-'}
                          </p>
                        </div>
                      </div>

                      {/* Résumé OCR */}
                      {courrier.summaryText && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Résumé OCR</label>
                          <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded">{courrier.summaryText}</p>
                        </div>
                      )}

                      {/* Métadonnées */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Métadonnées</label>
                        <pre className="bg-gray-50 text-gray-800 text-xs rounded p-3 overflow-auto">
{JSON.stringify(courrier.metadata ?? {}, null, 2)}
                        </pre>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {activeTab === 'pieces' && (
                  <Card
                    title="Pièces jointes"
                    actions={
                      <>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          onChange={handlePieceFileChange}
                        />
                        <Button
                          size="sm"
                          onClick={handleAddPieceClick}
                          isLoading={uploadingPiece}
                          disabled={uploadingPiece}
                        >
                          Ajouter une pièce
                        </Button>
                      </>
                    }
                  >
                    <CardContent>
                      {piecesLoading ? (
                        <div className="animate-pulse space-y-3">
                          {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-16 bg-gray-200 rounded"></div>
                          ))}
                        </div>
                      ) : pieces.length > 0 ? (
                        <div className="space-y-3">
                          {pieces.map((piece) => (
                            <div key={piece.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border border-gray-200 rounded gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{piece.filename}</p>
                                <p className="text-sm text-gray-500">
                                  {piece.mime} • {Math.round(piece.size / 1024)} KB
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2 justify-end">
                                {(piece.mime?.startsWith('image/') || piece.mime === 'application/pdf') && (
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    className="flex-1 sm:flex-none min-w-[120px]"
                                    onClick={async () => {
                                      try {
                                        // Récupérer le fichier via une requête authentifiée avec apiClient
                                        const response = await apiClient.get(`/pieces/${piece.id}/file`, {
                                          responseType: 'blob'
                                        });
                                        
                                        // Créer une URL blob pour la prévisualisation
                                        const blobUrl = URL.createObjectURL(response);
                                        
                                        setImageModalSrc(blobUrl);
                                        setImageModalTitle((piece as any).original_name || piece.filename);
                                        setImageModalOpen(true);
                                      } catch (error) {
                                        console.error('Erreur lors du chargement de la prévisualisation:', error);
                                        // Utiliser l'état d'erreur existant du composant
                                        setError('Impossible de charger la prévisualisation');
                                      }
                                    }}
                                  >
                                    Prévisualiser
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1 sm:flex-none min-w-[120px]"
                                  onClick={() => downloadPiece(piece.id)}
                                >
                                  Télécharger
                                </Button>
                                {piece.mime?.startsWith('image/') && (
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    className="flex-1 sm:flex-none min-w-[80px]"
                                    onClick={() => {
                                      // Ouvrir la modale OCR avec l'état de chargement
                                      setOcrModalTitle(piece.original_name || piece.filename);
                                      setOcrText('');
                                      setOcrError('');
                                      setOcrConfidence(null);
                                      setOcrStatus('PENDING');
                                      setOcrCopyOk(false);
                                      setOcrLoading(true);
                                      setOcrModalOpen(true);

                                      // Déclencher l'OCR
                                      triggerOCR(piece.id)
                                        .then(() => {
                                          // Après le déclenchement, commencer à vérifier le statut
                                          const checkStatus = async () => {
                                            try {
                                              const ocrResult = await getOCRText(piece.id);
                                              setOcrConfidence(
                                                typeof ocrResult?.confidence === 'number'
                                                  ? ocrResult.confidence
                                                  : null
                                              );
                                              setOcrStatus(ocrResult?.ocrStatus ?? null);

                                              if (ocrResult.ocrStatus === 'COMPLETED') {
                                                setOcrText(ocrResult.text || '');
                                                setOcrLoading(false);
                                              } else if (ocrResult.ocrStatus === 'FAILED') {
                                                setOcrError('Échec du traitement OCR');
                                                setOcrLoading(false);
                                              } else {
                                                // PENDING/PROCESSING: continuer à vérifier après 2 secondes
                                                setTimeout(checkStatus, 2000);
                                              }
                                            } catch (error) {
                                              console.error('Erreur lors de la récupération du texte OCR:', error);
                                              setOcrError('Erreur lors de la récupération du texte OCR');
                                              setOcrLoading(false);
                                            }
                                          };

                                          // Commencer à vérifier après 2 secondes
                                          setTimeout(checkStatus, 2000);
                                        })
                                        .catch((error) => {
                                          console.error('Erreur lors du déclenchement de l\'OCR:', error);
                                          setOcrError('Erreur lors du déclenchement de l\'OCR');
                                          setOcrLoading(false);
                                        });
                                    }}
                                  >
                                    OCR
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">
                          Aucune pièce jointe
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {activeTab === 'history' && (
                  <Card title="Historique des événements">
                    <CardContent>
                      <WorkflowHistory
                        events={history?.map(event => ({
                          ...event,
                          from_status: event.from_status === 'recu' ? 'RECU' as const :
                                      event.from_status === 'en_validation' ? 'EN_VALIDATION' as const :
                                      event.to_status as any,
                          to_status: event.to_status === 'en_validation' ? 'EN_VALIDATION' as const :
                                    event.to_status as any
                        })) || []}
                        isLoading={historyLoading}
                      />
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>

          {/* Actions rapides */}
          <div className="space-y-6">
            <Card title="Workflow">
              <CardContent>
                <WorkflowTransition
                  courrierId={courrier.id}
                  currentStatus={courrier.status}
                  onTransition={(newStatus) => {
                    // La mutation dans WorkflowTransition gère déjà l'invalidation des queries
                    console.log('Status changed to:', newStatus);
                  }}
                />
              </CardContent>
            </Card>

            <Card title="Métadonnées">
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Créé le:</span>
                  <span>{new Date(courrier.createdAt).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Modifié le:</span>
                  <span>{new Date(courrier.updatedAt).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Créé par:</span>
                  <span>{getUserName(courrier.createdBy)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      {/* Modale d'édition */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Modifier le courrier"
        size="large"
      >
        <form id="edit-courrier-form" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Référence</label>
              <input
                type="text"
                name="reference"
                defaultValue={courrier.reference}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Référence du courrier"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Statut</label>
              <select
                name="status"
                defaultValue={courrier.status}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="RECU">Reçu</option>
                <option value="EN_VALIDATION">En validation</option>
                <option value="INCOMPLET">Incomplet</option>
                <option value="EN_ATTENTE_SIGNATURE">En attente de signature</option>
                <option value="SIGNE">Signé</option>
                <option value="ENVOYE">Envoyé</option>
                <option value="CLASSE">Classé</option>
                <option value="REJETE">Rejeté</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Priorité</label>
              <select
                name="priority"
                defaultValue={courrier.priority}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="LOW">Basse</option>
                <option value="NORMAL">Normale</option>
                <option value="HIGH">Haute</option>
                <option value="URGENT">Urgente</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Échéance</label>
              <input
                type="datetime-local"
                name="dueDate"
                defaultValue={formatDateTimeForInput(courrier.dueDate)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Expéditeur</label>
              <input
                type="text"
                name="sender"
                defaultValue={courrier.sender}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Expéditeur"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Destinataire</label>
              <input
                type="text"
                name="recipient"
                defaultValue={courrier.recipient}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Destinataire"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Sujet</label>
              <input
                type="text"
                name="subject"
                defaultValue={courrier.subject}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Sujet du courrier"
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              type="button"
            >
              Annuler
            </Button>
            <Button
              onClick={(e) => {
                e.preventDefault();
                // Récupérer les valeurs du formulaire
                const form = document.querySelector('#edit-courrier-form') as HTMLFormElement;
                if (form) {
                  const formData = new FormData(form);
                  const updates: UpdateCourrierRequest = {
                    reference: formData.get('reference') as string,
                    status: formData.get('status') as CourrierStatus,
                    priority: formData.get('priority') as CourrierPriority,
                    dueDate: formData.get('dueDate') ? new Date(formData.get('dueDate') as string).toISOString() : undefined,
                    sender: formData.get('sender') as string,
                    recipient: formData.get('recipient') as string,
                    subject: formData.get('subject') as string,
                  };
                  updateMutation.mutate(updates);
                }
              }}
              isLoading={updateMutation.isPending}
              type="submit"
            >
              Enregistrer
            </Button>
          </div>
        </form>
      </Modal>
      </div>
  );
}